import type { ProviderId, TileType } from './constants.js';

/**
 * Cache-key derivation (ADR-001 §3, ADR-002 §5).
 *
 * The shared cache is the D-007 cost mechanism: cost scales with distinct
 * `(type, provider, params)` tuples, NOT users. That only works if the key is
 * CANONICAL — two requests for the same underlying query must produce byte-identical
 * keys regardless of:
 *   - param insertion order   ({lat,lon} === {lon,lat})
 *   - list order within params (['AAPL','MSFT'] === ['MSFT','AAPL'] — same basket)
 *
 * The key is a plain, bounded, URL/KV-safe string. It is derived from
 * `(type, provider, normalizedParams)` ONLY — never from `v`, `id`, display, or
 * label (those don't change which upstream query runs).
 */

export interface CacheKeyInput {
  readonly type: TileType;
  readonly provider: ProviderId;
  readonly params: Readonly<Record<string, unknown>>;
}

/** A plain (non-null, non-array) object — the only "record" shape our params use. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Canonicalize a JSON-ish value into a stable string:
 *  - object keys sorted lexicographically
 *  - SCALAR arrays sorted + de-duplicated (order-independent SET, e.g. symbol
 *    baskets: ['AAPL','MSFT'] === ['MSFT','AAPL'] === ['AAPL','AAPL','MSFT'])
 *  - OBJECT arrays kept in ORDER, with duplicates preserved (positional SEQUENCE,
 *    e.g. todo items: reordering IS a different value; two rows with identical
 *    {text,done} are two distinct rows). See the "ordered vs set" note below.
 *  - primitives rendered deterministically
 * Pure; does not mutate input (spreads into fresh arrays/objects).
 *
 * ORDERED vs SET (ADR-002 §5, extended for local widgets 2026-07-25):
 *   The two array semantics reflect what the underlying value MEANS.
 *   - A `markets.quote` `symbols` list is a BASKET: order and duplicates are
 *     noise, the same set of tickers is the same upstream query — SET semantics
 *     keep the shared-cache dedupe honest (D-007).
 *   - A `todo.list` `items` list is a SEQUENCE: the user's ordering is content
 *     (a reprioritized list is a different board state) and duplicate rows are
 *     legitimate — SEQUENCE semantics keep the board-identity key injective so
 *     that reordering or dup-ing items yields a DIFFERENT key, as it must.
 *   The discriminator is the element kind: arrays of plain objects are the only
 *   sequences our strict param schemas produce; every scalar array is a set.
 *   (Local widgets never key the SHARED cache — they are clientDirect/noFetch —
 *   but their configs still derive a stable, injective key for board storage.)
 */
function canonicalize(value: unknown): string {
  if (value === undefined) return 'undefined'; // explicit — JSON.stringify(undefined) is not a string
  if (value === null) return 'null';
  if (Array.isArray(value)) {
    const parts = value.map(canonicalize);
    // OBJECT arrays are positional sequences: preserve order AND duplicates so the
    // key is injective over ordering/duplication (todo items). An empty array has
    // no elements to inspect; treat it as the (identical) empty sequence/set — `[]`
    // canonicalizes the same either way, so the choice is immaterial for [].
    if (value.length > 0 && value.every(isPlainObject)) {
      return `[${parts.join(',')}]`;
    }
    // SCALAR arrays behave as a SET: sort AND de-duplicate (symbol baskets).
    const uniqueSorted = [...new Set(parts)].sort();
    return `[${uniqueSorted.join(',')}]`;
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    const entries = keys.map((k) => `${JSON.stringify(k)}:${canonicalize(obj[k])}`);
    return `{${entries.join(',')}}`;
  }
  // Primitives: numbers/booleans/strings — JSON.stringify gives a stable rendering.
  return JSON.stringify(value);
}

/**
 * Make a string URL/KV-safe by ENCODING (not stripping) unsafe characters. This
 * MUST be injective: two different inputs must never collapse to the same output,
 * or two distinct queries would share a cache entry (cache poisoning — the whole
 * point of a canonical key is defeated). We keep an unreserved readable set and
 * percent-encode everything else via `encodeURIComponent`, which is a bijection on
 * the remaining characters. `%` itself is in the reserved set encodeURIComponent
 * escapes, so the encoding cannot be spoofed by a literal `%` in the input.
 */
function encodeKeySegment(s: string): string {
  // Preserve the structural delimiters we chose (| { } [ ] , : . _ - ") so the key
  // stays human-readable, and encode the rest. All preserved chars are produced
  // only by our own canonical form, never smuggled from params (params strings are
  // JSON.stringify'd, so a literal `"` inside a value becomes `\"` — distinct).
  let out = '';
  for (const ch of s) {
    if (/[A-Za-z0-9._:\-|{}\[\],"]/.test(ch)) {
      out += ch;
    } else {
      out += encodeURIComponent(ch);
    }
  }
  return out;
}

/**
 * Derive the canonical cache key for one upstream query.
 *
 * Format: `v1|<type>|<provider>|<canonical-params>`. The `v1` prefix is the
 * CACHE-KEY format version (bump only if the derivation algorithm changes — lets
 * us invalidate the whole cache namespace cleanly), independent of tile schema `v`.
 *
 * INJECTIVE by construction: canonicalization is deterministic and the final
 * encoding is a bijection, so distinct `(type, provider, params)` tuples yield
 * distinct keys (no cross-query collision).
 */
export function deriveCacheKey(input: CacheKeyInput): string {
  const params = canonicalize(input.params);
  const raw = `v1|${input.type}|${input.provider}|${params}`;
  return encodeKeySegment(raw);
}
